import { eq } from "@-/com.npmjs.drizzle-orm";
import type { ActorDatabase } from "@-/dev.improve.haniverse.database.actor";
import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import {
  addPendingCompletedSlideKey,
  removePendingCompletedSlideKey,
} from "@-/dev.improve.haniverse.lib.slide.read";
import { ensureUserSlideRow } from "@-/dev.improve.haniverse.lib.slide.row";
import { recordSlideCompletionPrior } from "#slide-completion-priors";
import { enqueueSlideCompletion } from "#slide-completion-queue";

/**
 * Marks an authored slide complete in the `user_slide` table, keyed by its catalogue UUID `key`.
 * The row stores only that key and timestamps, never slide content: slides ship in the app bundle
 * and may be edited in any release.
 *
 * The caller does not wait for the row. Nothing the learner sees next depends on it, because the
 * next slide comes from the bundled catalogue. So the slide reads as complete at once, through the
 * pending-key buffer that every completion read merges, and the row is written behind on the serial
 * slide-completion queue. A failed write withdraws that mark and is logged; the returned promise
 * settles when the write does and never rejects.
 *
 * `completionId` names this completion so {@link restoreSlideCompletion} can take exactly it back.
 * The queued write records what it found before writing, and a restore runs behind it on the same
 * queue, so it always finds that record.
 *
 * Idempotent: an existing live row gets a fresh `completedAt` rather than a duplicate. A missing
 * row is created blank and then completed (`ensureUserSlideRow`), so the completion reaches the
 * cloud as a change to `completedAt` alone.
 */
export function completeSlide({
  actorDb,
  completionId,
  key,
  now,
}: {
  actorDb: ActorDatabase;
  completionId: string;
  key: string;
  now: Date;
}): Promise<void> {
  addPendingCompletedSlideKey(key);
  return enqueueSlideCompletion(async () => {
    const nowIso = now.toISOString();
    await actorDb(import.meta.location).transaction(async (tx) => {
      const existing = await tx.query.userSlide.findFirst({
        columns: { completedAt: true },
        where: { key },
      });
      recordSlideCompletionPrior(completionId, existing?.completedAt ?? null);
      const id = await ensureUserSlideRow(tx, key, nowIso);
      await tx
        .update(actorSchema.userSlide)
        .set({ completedAt: nowIso })
        .where(eq(actorSchema.userSlide.id, id));
    });
  }).catch((error: unknown) => {
    // A completion that never answered, including one past the queue's deadline, must not leave
    // the slide looking complete. A late write that still lands is picked up by the next read of
    // `user_slide`.
    removePendingCompletedSlideKey(key);
    logger(import.meta.location).error("Slide completion failed", { error, key });
  });
}
